import { lazy, Suspense, useEffect, type ComponentType } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/stores/auth';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { NotFound } from '@/pages/NotFound';

/**
 * lazy() wrapper that auto-reloads on a chunk-load failure.
 *
 * When we deploy, Vite emits new content-hashed chunk filenames. Tabs
 * still open from the previous deploy hold references to the OLD hashes,
 * and the moment they navigate to a lazy-loaded route the import rejects
 * with "Failed to fetch dynamically imported module". One hard reload
 * pulls fresh HTML referencing the new hashes, fixing the session.
 *
 * sessionStorage guards against a reload loop if the failure is genuine
 * (e.g. the chunk is actually broken) — we only auto-reload once per
 * session.
 */
const CHUNK_RELOAD_KEY = 'haphe_chunk_reload_at';

function lazyWithReload<T extends { default: ComponentType<unknown> }>(
  factory: () => Promise<T>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const isChunkLoadError =
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /Importing a module script failed/i.test(msg) ||
        /error loading dynamically imported module/i.test(msg);
      if (isChunkLoadError && typeof window !== 'undefined') {
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? '0');
        if (Date.now() - last > 10_000) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
          window.location.reload();
          // Hang until the reload swaps the page so React doesn't paint
          // an error boundary in the meantime.
          return new Promise<T>(() => {});
        }
      }
      throw err;
    }
  });
}

// Code-split every top-level route. A card visitor downloads only the
// PublicCard chunk; a marketing visitor only Landing; the editor only loads
// behind login. Before this, Landing + PublicCard + Template all sat in the
// entry chunk, so every visitor paid for all of them.
const MarketingLayout = lazyWithReload(() => import('@/marketing/MarketingLayout').then((m) => ({ default: m.MarketingLayout })));
const Home = lazyWithReload(() => import('@/marketing/pages/Home').then((m) => ({ default: m.Home })));
const CardsPage = lazyWithReload(() => import('@/marketing/pages/Cards').then((m) => ({ default: m.CardsPage })));
const ShopPage = lazyWithReload(() => import('@/marketing/pages/Shop').then((m) => ({ default: m.ShopPage })));
const LeadsPage = lazyWithReload(() => import('@/marketing/pages/Leads').then((m) => ({ default: m.LeadsPage })));
const GrowPage = lazyWithReload(() => import('@/marketing/pages/Grow').then((m) => ({ default: m.GrowPage })));
const PricingPage = lazyWithReload(() => import('@/marketing/pages/Pricing').then((m) => ({ default: m.PricingPage })));
const Login = lazyWithReload(() => import('@/pages/Login').then((m) => ({ default: m.Login })));
const AuthCallback = lazyWithReload(() => import('@/pages/AuthCallback').then((m) => ({ default: m.AuthCallback })));
const ResetPassword = lazyWithReload(() => import('@/pages/ResetPassword').then((m) => ({ default: m.ResetPassword })));
const PublicCard = lazyWithReload(() => import('@/pages/PublicCard').then((m) => ({ default: m.PublicCard })));
const Template = lazyWithReload(() => import('@/pages/Template').then((m) => ({ default: m.Template })));
const OrderStatus = lazyWithReload(() => import('@/pages/OrderStatus').then((m) => ({ default: m.OrderStatus })));
const Terms = lazyWithReload(() => import('@/pages/legal/Terms').then((m) => ({ default: m.Terms })));
const Privacy = lazyWithReload(() => import('@/pages/legal/Privacy').then((m) => ({ default: m.Privacy })));

// Code-split the protected app shell — keeps the marketing bundle lean.
const AppLayout = lazyWithReload(() => import('@/components/layouts/AppLayout').then((m) => ({ default: m.AppLayout })));
const Dashboard = lazyWithReload(() => import('@/pages/app/Dashboard').then((m) => ({ default: m.Dashboard })));
const CardsDeck = lazyWithReload(() => import('@/pages/app/CardsDeck').then((m) => ({ default: m.CardsDeck })));
const CardEditor = lazyWithReload(() => import('@/pages/app/CardEditor').then((m) => ({ default: m.CardEditor })));
const Leads = lazyWithReload(() => import('@/pages/app/Leads').then((m) => ({ default: m.Leads })));
const Orders = lazyWithReload(() => import('@/pages/app/Orders').then((m) => ({ default: m.Orders })));
const Settings = lazyWithReload(() => import('@/pages/app/Settings').then((m) => ({ default: m.Settings })));
const Admin = lazyWithReload(() => import('@/pages/app/Admin').then((m) => ({ default: m.Admin })));

function AppLoading() {
  return (
    <div className="flex h-screen items-center justify-center bg-bone">
      <div className="h-1 w-24 overflow-hidden rounded-full bg-bone-deep">
        <div className="h-full w-1/3 animate-pulse bg-accent" />
      </div>
    </div>
  );
}

export default function App() {
  const init = useAuth((s) => s.init);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <Routes>
      {/* Marketing + public */}
      <Route element={<Suspense fallback={<AppLoading />}><MarketingLayout /></Suspense>}>
        <Route path="/" element={<Suspense fallback={<AppLoading />}><Home /></Suspense>} />
        <Route path="/features" element={<Navigate to="/features/cards" replace />} />
        <Route path="/features/cards" element={<Suspense fallback={<AppLoading />}><CardsPage /></Suspense>} />
        <Route path="/features/shop" element={<Suspense fallback={<AppLoading />}><ShopPage /></Suspense>} />
        <Route path="/features/leads" element={<Suspense fallback={<AppLoading />}><LeadsPage /></Suspense>} />
        <Route path="/features/grow" element={<Suspense fallback={<AppLoading />}><GrowPage /></Suspense>} />
        <Route path="/pricing" element={<Suspense fallback={<AppLoading />}><PricingPage /></Suspense>} />
      </Route>
      <Route path="/u/*" element={<Suspense fallback={<AppLoading />}><PublicCard /></Suspense>} />
      <Route path="/t/:token" element={<Suspense fallback={<AppLoading />}><Template /></Suspense>} />
      <Route path="/o/:id" element={<Suspense fallback={<AppLoading />}><OrderStatus /></Suspense>} />

      {/* Legal */}
      <Route path="/terms" element={<Suspense fallback={<AppLoading />}><Terms /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<AppLoading />}><Privacy /></Suspense>} />

      {/* Auth */}
      <Route path="/login" element={<Suspense fallback={<AppLoading />}><Login /></Suspense>} />
      <Route path="/signup" element={<Navigate to="/login?mode=signup" replace />} />
      <Route path="/auth/callback" element={<Suspense fallback={<AppLoading />}><AuthCallback /></Suspense>} />
      <Route path="/auth/reset" element={<Suspense fallback={<AppLoading />}><ResetPassword /></Suspense>} />

      {/* App (protected) */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Suspense fallback={<AppLoading />}>
              <AppLayout />
            </Suspense>
          </ProtectedRoute>
        }
      >
        <Route index element={<Suspense fallback={<AppLoading />}><Dashboard /></Suspense>} />
        <Route path="cards" element={<Suspense fallback={<AppLoading />}><CardsDeck /></Suspense>} />
        <Route path="cards/:id/edit" element={<Suspense fallback={<AppLoading />}><CardEditor /></Suspense>} />
        <Route path="leads" element={<Suspense fallback={<AppLoading />}><Leads /></Suspense>} />
        <Route path="orders" element={<Suspense fallback={<AppLoading />}><Orders /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<AppLoading />}><Settings /></Suspense>} />
        <Route path="admin" element={<Suspense fallback={<AppLoading />}><Admin /></Suspense>} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
